import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import {
  HomeIcon,
  DocumentTextIcon,
  UsersIcon,
  CreditCardIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon, roles: ['admin', 'billing_officer', 'approver', 'supervisor', 'reconciliation_officer', 'auditor', 'it_support'] },
  { name: 'Bills', href: '/bills', icon: DocumentTextIcon, roles: ['admin', 'billing_officer', 'approver', 'supervisor'] },
  { name: 'Customers', href: '/customers', icon: UsersIcon, roles: ['admin', 'billing_officer'] },
  { name: 'Payments', href: '/payments', icon: CreditCardIcon, roles: ['admin', 'finance', 'reconciliation_officer'] },
  { name: 'Reconciliation', href: '/reconciliation', icon: ArrowsRightLeftIcon, roles: ['admin', 'reconciliation_officer'] },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon, roles: ['admin', 'finance', 'auditor'] },
  { name: 'Configuration', href: '/configuration', icon: CogIcon, roles: ['admin', 'system_admin'] },
  { name: 'API Clients', href: '/api-clients', icon: CogIcon, roles: ['admin', 'system_admin'] },
  { name: 'Audit Log', href: '/audit-log', icon: ClipboardDocumentListIcon, roles: ['admin', 'auditor'] },
  { name: 'Users', href: '/users', icon: UserGroupIcon, roles: ['admin'] },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();

  // Notifications states
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const notiRef = useRef(null);

  const fetchInAppNotifications = useCallback(() => {
    if (!user) return;
    api.get('/notifications/system')
      .then(res => {
        setNotifications(res.data?.data?.notifications || []);
        setUnreadCount(res.data?.data?.unreadCount || 0);
      })
      .catch(err => {
        console.error('Error fetching system notifications:', err);
      });
  }, [user]);

  // Poll notifications every 20 seconds
  useEffect(() => {
    fetchInAppNotifications();
    const interval = setInterval(fetchInAppNotifications, 20000);
    return () => clearInterval(interval);
  }, [fetchInAppNotifications]);

  // Click outside listener for notifications dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (notiRef.current && !notiRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/notifications/system/read/${id}`);
      fetchInAppNotifications();
    } catch (err) {
      console.error('Failed to mark read', err);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation();
    try {
      await api.post('/notifications/system/read-all');
      fetchInAppNotifications();
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  };

  const handleNotificationClick = (noti) => {
    if (!noti.isRead) {
      api.post(`/notifications/system/read/${noti.id}`)
        .then(() => fetchInAppNotifications())
        .catch(err => console.error('Failed to mark read', err));
    }
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [darkMode]);

  const filteredNavigation = navigation.filter(item => hasRole(item.roles));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 transition-colors duration-300 dark:bg-slate-955 dark:text-slate-100">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-xs" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex w-64 flex-col bg-slate-900 shadow-xl">
            <div className="flex h-16 items-center justify-between px-4 border-b border-slate-800">
              <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-indigo-500 bg-clip-text text-transparent">Xpert Payments</span>
              <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-white rounded-lg p-1 transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`sidebar-item ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex min-h-0 flex-1 flex-col bg-slate-900 dark:bg-slate-950 border-r border-slate-800">
          <div className="flex h-16 items-center px-6 border-b border-slate-800">
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-400 to-indigo-500 bg-clip-text text-transparent">Xpert Payments</span>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${isActive ? 'sidebar-item-active' : 'sidebar-item-inactive'}`}
                >
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/60 dark:bg-slate-900/80 dark:border-slate-850 transition-colors duration-300">
          <div className="flex h-16 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 rounded-lg"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            <div className="flex items-center gap-4 ml-auto">
              {/* Interactive Theme Toggler */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-850 transition-all duration-200"
                title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {darkMode ? <SunIcon className="h-5 w-5 animate-pulse" /> : <MoonIcon className="h-5 w-5" />}
              </button>

              {/* Notifications bell dropdown */}
              <div className="relative" ref={notiRef}>
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-850 relative transition-all duration-200"
                  title="Notifications"
                >
                  <BellIcon className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <>
                      <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-indigo-500 animate-ping" />
                      <span className="absolute top-1.5 right-1.5 h-2.5 w-2.5 rounded-full bg-indigo-500" />
                    </>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-hidden rounded-md bg-white dark:bg-slate-900 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 border border-slate-200 dark:border-slate-800 flex flex-col animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/40">
                      <span className="text-xs font-semibold text-slate-900 dark:text-white">Notifications ({unreadCount} unread)</span>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="text-[10px] font-bold text-indigo-650 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-305"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    <div className="overflow-y-auto max-h-72 divide-y divide-slate-100 dark:divide-slate-830">
                      {notifications.length === 0 ? (
                        <div className="px-4 py-6 text-center text-xs text-slate-400 dark:text-slate-500">
                          No alerts or notifications
                        </div>
                      ) : (
                        notifications.map((noti) => (
                          <div
                            key={noti.id}
                            onClick={() => handleNotificationClick(noti)}
                            className={`p-3 text-left transition-colors flex gap-2 cursor-pointer ${noti.isRead
                              ? 'bg-white hover:bg-slate-55 dark:bg-slate-900 dark:hover:bg-slate-850/50'
                              : 'bg-indigo-50/20 hover:bg-indigo-50/40 dark:bg-indigo-950/10 dark:hover:bg-indigo-955/20'
                              }`}
                          >
                            <div className="mt-1 flex-shrink-0">
                              {noti.type === 'success' && <span className="h-2 w-2 rounded-full bg-green-500 block" />}
                              {noti.type === 'error' && <span className="h-2 w-2 rounded-full bg-red-500 block" />}
                              {noti.type === 'warning' && <span className="h-2 w-2 rounded-full bg-yellow-500 block" />}
                              {noti.type === 'info' && <span className="h-2 w-2 rounded-full bg-blue-500 block" />}
                            </div>
                            <div className="flex-1 space-y-0.5 min-w-0">
                              <p className={`text-xs truncate ${noti.isRead ? 'font-medium text-slate-800 dark:text-slate-200' : 'font-bold text-slate-900 dark:text-white'}`}>
                                {noti.title}
                              </p>
                              <p className="text-[11px] text-slate-550 dark:text-slate-400 break-words line-clamp-3">{noti.message}</p>
                              <p className="text-[9px] text-slate-400 dark:text-slate-500 font-mono mt-1">{new Date(noti.createdAt).toLocaleTimeString()}</p>
                            </div>
                            {!noti.isRead && (
                              <button
                                onClick={(e) => handleMarkAsRead(noti.id, e)}
                                className="text-[10px] text-indigo-650 hover:text-indigo-850 dark:text-indigo-400 self-start pr-1"
                                title="Mark read"
                              >
                                Read
                              </button>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="h-6 w-px bg-slate-200 dark:bg-slate-800" />

              <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-semibold text-slate-855 dark:text-slate-200">{user?.username}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <button
                  onClick={logout}
                  className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-all duration-200"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
